<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAdmin } from '@/stores/admin'
import { advertiserCreateSchema } from '@/views/forms/schema'
import type { FormSubmitEvent } from '@nuxt/ui'
import type { z } from 'zod'

const admin = useAdmin()

const emit = defineEmits<{
  submit: [
    data: {
      brand_id: string
      platform_id: string
      platform_advertiser_identifier: string
      country_code: string | null
      sector_ids: string[]
    },
  ]
  cancel: []
}>()

const state = ref({
  brand_id: '',
  platform_id: '',
  platform_advertiser_identifier: '',
  country_code: null as string | null,
  sector_ids: [] as string[],
})

// Brand search
const brandSearch = ref('')
const filteredBrands = computed(() => {
  if (!brandSearch.value) return admin.brands.slice(0, 50)
  const q = brandSearch.value.toLowerCase()
  return admin.brands.filter((b) => b.name.toLowerCase().includes(q)).slice(0, 50)
})

// All sectors available (adding a non-brand sector auto-adds it to the brand)
const availableSectors = computed(() => admin.sectors)

// Clear sectors when brand changes
watch(
  () => state.value.brand_id,
  () => {
    state.value.sector_ids = []
  },
)

// Platform options (leaf platforms only - no parents)
const platformOptions = computed(() =>
  admin.platforms
    .filter((p) => !admin.platforms.some((child) => child.parent_platform_id === p.id))
    .map((p) => ({ label: p.name, value: p.id })),
)

// Country options
const countrySearch = ref('')
const filteredCountries = computed(() => {
  const countries = [
    { label: 'Global (no country)', value: null },
    ...admin.countries.map((c) => ({ label: `${c.name_en} (${c.code})`, value: c.code })),
  ]
  if (!countrySearch.value) return countries.slice(0, 50)
  const q = countrySearch.value.toLowerCase()
  return countries.filter((c) => c.label.toLowerCase().includes(q)).slice(0, 50)
})

// Selected platform's hint
const selectedPlatform = computed(() =>
  admin.platforms.find((p) => p.id === state.value.platform_id),
)
const identifierHint = computed(
  () => selectedPlatform.value?.advertiser_id_hint ?? 'Platform advertiser identifier',
)

function onSubmit(event: FormSubmitEvent<z.output<typeof advertiserCreateSchema>>) {
  emit('submit', {
    brand_id: event.data.brand_id,
    platform_id: event.data.platform_id,
    platform_advertiser_identifier: event.data.platform_advertiser_identifier,
    country_code: event.data.country_code ?? null,
    sector_ids: event.data.sector_ids ?? [],
  })
}

const isSubmitDisabled = computed(
  () =>
    !state.value.brand_id ||
    !state.value.platform_id ||
    !state.value.platform_advertiser_identifier.trim(),
)
</script>

<template>
  <div class="p-4 space-y-6">
    <UForm :schema="advertiserCreateSchema" :state="state" class="space-y-4" @submit="onSubmit">
      <UFormField label="Brand" name="brand_id" required>
        <USelectMenu
          v-model="state.brand_id"
          v-model:query="brandSearch"
          :items="filteredBrands"
          value-key="id"
          label-key="name"
          placeholder="Search brands..."
          searchable
          class="w-full"
        />
      </UFormField>

      <UFormField label="Platform" name="platform_id" required>
        <USelectMenu
          v-model="state.platform_id"
          :items="platformOptions"
          value-key="value"
          label-key="label"
          placeholder="Select platform"
          class="w-full"
        />
      </UFormField>

      <UFormField label="Identifier" name="platform_advertiser_identifier" required>
        <UInput
          v-model="state.platform_advertiser_identifier"
          :placeholder="identifierHint"
          class="w-full"
        />
        <template #hint v-if="selectedPlatform?.advertiser_id_hint">
          <span class="text-xs text-muted">{{ selectedPlatform.advertiser_id_hint }}</span>
        </template>
      </UFormField>

      <UFormField label="Country" name="country_code">
        <USelectMenu
          v-model="state.country_code"
          v-model:query="countrySearch"
          :items="filteredCountries"
          value-key="value"
          label-key="label"
          placeholder="Global (no country)"
          searchable
          class="w-full"
        />
        <template #hint>
          <span class="text-xs text-muted">Leave empty for global advertisers</span>
        </template>
      </UFormField>

      <UFormField v-if="availableSectors.length > 0" label="Sectors" name="sector_ids">
        <USelectMenu
          v-model="state.sector_ids"
          :items="availableSectors"
          value-key="id"
          label-key="name"
          placeholder="Select sectors..."
          multiple
          class="w-full"
        />
        <template #hint>
          <span class="text-xs text-muted">Non-brand sectors will be added to the brand</span>
        </template>
      </UFormField>
      <div v-else-if="state.brand_id" class="text-xs text-muted">No sectors available</div>

      <div class="flex justify-end gap-2 pt-4">
        <UButton type="button" color="neutral" variant="soft" @click="emit('cancel')">
          Cancel
        </UButton>
        <UButton type="submit" color="primary" :disabled="isSubmitDisabled">
          Create Advertiser
        </UButton>
      </div>
    </UForm>
  </div>
</template>
